import moment from "moment";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import TIcon from "../../components/ui/TIcon";
import * as SBService from "../../helpers/sportbookService";
import { getAssetFullUrl } from "../../helpers/utils";
import {
  NEWS_TYPE_LABELS,
  NewsArticleInterface,
  NewsTypeValue,
} from "../../interfaces/news";
import { ArticleCard } from ".";

/**
 * Màn bài viết, tra theo slug trên URL.
 *
 * Công khai như màn danh sách: đây chính là link được chia sẻ ra ngoài, đá về màn
 * đăng nhập là hỏng hết mục đích.
 */
const NewsDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticleInterface | null>(null);
  const [related, setRelated] = useState<NewsArticleInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadRelated = async (current: NewsArticleInterface) => {
      try {
        const rsp = await SBService.getPublicNews({ type: current.type, limit: 4 });
        const data: NewsArticleInterface[] = rsp?.data ?? [];
        setRelated(data.filter((item) => item.id !== current.id).slice(0, 3));
      } catch (e: any) {
        // Bài liên quan chỉ là phần phụ: hỏng thì bỏ qua, không phá trang đang đọc.
        setRelated([]);
      }
    };

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const rsp = await SBService.getPublicNewsBySlug(slug as string);
        // Bài lẻ bị API bọc trong `data`, còn danh sách thì không - nhận cả hai
        // dạng để không phụ thuộc vào chi tiết đó.
        const found: NewsArticleInterface = rsp?.data ?? rsp;
        if (!found?.id) {
          setNotFound(true);
          setLoading(false);

          return;
        }
        setArticle(found);
        loadRelated(found);
      } catch (e: any) {
        // 404 là bài không có thật hoặc chưa đăng, không phải lỗi hệ thống.
        setNotFound(true);
      }
      setLoading(false);
    };

    if (slug) {
      load();
    }
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = "Biadi — " + article.title;
    }
  }, [article]);

  if (loading) {
    return <div className="main app-shell no-tabbar px-5 relative top-0" />;
  }

  if (notFound || !article) {
    return (
      <div className="main app-shell no-tabbar px-5 relative top-0">
        <header className="page-head bleed" style={{ justifyContent: "flex-start", gap: 10 }}>
          <button type="button" className="icon-btn" onClick={() => navigate("/news")}>
            <TIcon name="chevron-left" />
          </button>
          <h1>TIN TỨC</h1>
        </header>
        <div className="ui-card empty-state" style={{ marginTop: 22 }}>
          <TIcon name="file-off" />
          <div className="t">Bài viết không tồn tại</div>
          <div className="h">Bài có thể đã được gỡ hoặc chưa được đăng.</div>
          <button
            type="button"
            className="chip active"
            style={{ marginTop: 12 }}
            onClick={() => navigate("/news")}
          >
            Về danh sách
          </button>
        </div>
      </div>
    );
  }

  const banner = getAssetFullUrl(article.image);
  const publishedAt = article.publishedAt
    ? moment(article.publishedAt).format("DD/MM/YYYY")
    : "";

  return (
    <div className="main app-shell no-tabbar px-5 relative top-0">
      {/* metaTitle/metaDescription do người soạn đặt ở backoffice; để trống thì
          dùng tiêu đề và tóm tắt, đúng như dòng gợi ý trên màn soạn bài. */}
      <Helmet>
        <title>{article.metaTitle || article.title}</title>
        <meta
          name="description"
          content={article.metaDescription || article.excerpt || ""}
        />
      </Helmet>

      <header className="page-head bleed" style={{ justifyContent: "flex-start", gap: 10 }}>
        <button type="button" className="icon-btn" onClick={() => navigate(-1)}>
          <TIcon name="chevron-left" />
        </button>
        <h1>
          {(NEWS_TYPE_LABELS[article.type as NewsTypeValue] || "").toUpperCase()}
        </h1>
      </header>

      {banner ? <img src={banner} alt="" className="article-banner" /> : null}

      <h2 className="article-title">{article.title}</h2>

      <div className="article-meta">
        {article.category ? <span className="chip">{article.category}</span> : null}
        {publishedAt ? <span>{publishedAt}</span> : null}
        {article.author ? <span>· {article.author}</span> : null}
      </div>

      {article.excerpt ? (
        <p className="article-excerpt">{article.excerpt}</p>
      ) : null}

      {/* Nội dung là HTML soạn trong trình soạn thảo của backoffice, chỉ quản trị
          viên ghi được - không phải nội dung người ngoài gửi lên. */}
      <div
        className="article-content"
        dangerouslySetInnerHTML={{ __html: article.content || "" }}
      />

      {related.length > 0 ? (
        <section className="section">
          <div className="section-head">
            <h2>BÀI VIẾT KHÁC</h2>
          </div>
          {related.map((item) => (
            <ArticleCard
              key={`related-${item.id}`}
              article={item}
              onOpen={() => navigate(`/news/${item.slug}`)}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
};

export default NewsDetailPage;
